import React, { useState } from 'react'
import classnames from 'classnames'
import type { PresentationBlock, PresentationOptionAdvanced } from 'paintscout'
import { makeStyles } from '@paintscout/ui'
import type { MuiTheme as Theme } from '@paintscout/ui'
import type { LabelsOption } from '@paintscout/util/builder'
import TwoColumnBlock from '../../blocks/TwoColumn/TwoColumnBlock'
import type { PresentationBlockProps } from '../../'
import { GLOBAL_FOOTER_BLOCK_KEY } from '../../util/presentationEditorDom'

/** The canonical shape lives on PresentationOptionAdvanced — re-used, not re-declared, so the two can't drift. */
export type GlobalFooterFields = NonNullable<PresentationOptionAdvanced['footer']>

export interface GlobalFooterProps {
  footer?: GlobalFooterFields
  /** Presentation.tsx's `editable` flag — gates the pill and inline editing. False in the customer view. */
  isEditable: boolean
  /** Whether the footer is the current canvas selection — a styling hook only, does not gate inline editing. */
  isSelected: boolean
  /** Required by PresentationBlockProps — the footer isn't a `quote`/`work-order` block, so it's unused, just threaded through. */
  objectLabels: LabelsOption
  presentationContentStyles?: any
  presetColors?: string[]
  setFieldValue: (field: string, value: any) => void
  submitForm: () => Promise<any>
}

const useStyles = makeStyles(
  (theme: Theme) => ({
    root: {
      position: 'relative',
      marginTop: 34,
      borderTop: '1px solid #E4E7EC'
    },
    /** Purple hover / teal focus — same tokens every other block uses (Presentation.tsx's `sectionWrapperHovered`/`focusBorder`). */
    hovered: {
      outline: `2px solid ${theme.palette.presentation.sectionBorder.hover}`,
      outlineOffset: -2
    },
    selected: {
      outline: `2px solid ${theme.palette.presentation.sectionBorder.focus}`,
      outlineOffset: -2
    },
    pill: {
      position: 'absolute',
      top: -12,
      left: 28,
      padding: '2px 10px',
      borderRadius: 999,
      background: theme.palette.primary.main,
      color: theme.palette.common.white,
      fontSize: '0.7rem',
      lineHeight: '18px'
    },
    /** Mirrors Presentation.tsx's own `classes.content` — the presentation theme's typography, same as every real block gets from BlockWrapper. */
    blockContent: (props: { presentationContentStyles?: any }) => ({
      ...props.presentationContentStyles,
      overflowWrap: 'break-word'
    }),
    /** The padding lives on the same element as the background color/theme, same as BlockWrapper's
     * `sectionStyles` div — otherwise a theme/custom background wouldn't cover the padded edges. */
    blockBackground: {
      padding: '26px 28px 24px'
    }
  }),
  { name: 'GlobalFooter' }
)

/**
 * Wraps TwoColumnBlock's content the same way Presentation.tsx's `BlockWrapper` wraps a real block's
 * content — background color + the presentation's own typography — minus everything BlockWrapper
 * does that doesn't apply here (padding-by-block-type, embedded-quote/Sherwin-Williams exceptions,
 * focus border, the tree's `id={block.key}`): the footer is always a plain text 2-column, never any
 * of those other cases.
 */
function FooterBlockWrapper(props: PresentationBlockProps) {
  const { children, block } = props as PresentationBlockProps & { children: React.ReactElement }
  const classes = useStyles({ presentationContentStyles: props.presentationContentStyles })
  const background = block?.background ?? {}

  return (
    <div className={classes.blockContent}>
      {/* `.themeBackground` is a nested selector inside `blockContent` (from `presentationContentStyles`,
          same as BlockWrapper's `classes.content`) — it only takes effect on a descendant, so the
          theme-background toggle needs this inner wrapper, same structure as a real block. */}
      <div
        className={classnames(classes.blockBackground, { themeBackground: background.useTheme })}
        style={background.color ? { backgroundColor: background.color } : undefined}
      >
        {children}
      </div>
    </div>
  )
}

/**
 * The global footer (#22211) — one shared, text-only two-column block rendered once, at the end of
 * Presentation.tsx's root div, so both the editor and the customer view get it automatically. Not a
 * page/section/block node: it has no tree row, no BlockMenu, and is persisted at `advanced.footer`,
 * not per page — the one thing that genuinely can't be a regular block, since the block tree has no
 * way to express "one block, rendered on every page."
 *
 * Everything else reuses the real block machinery: `footer.fields`/`footer.background` are shaped
 * exactly like a `2-column` block's, so this renders the actual `TwoColumnBlock` component
 * (`leftType`/`rightType` pinned to `'text'`) instead of hand-rolling text columns, and
 * `FooterInspectorPanel` composes the same `ColumnAlignmentOption`/`BackgroundColorOption` the real
 * `2-column` block's own options panel uses.
 *
 * Selection and inline field editing are deliberately decoupled, same pattern as every other block
 * (see Presentation.tsx's `isEditable: editable && focusedBlock === block.key`): a single click
 * selects the footer, and only once selected does TwoColumnBlock's own `isEditable` prop let its
 * InlineEditableHtml fields actually mount as editable.
 */
function GlobalFooter(props: GlobalFooterProps) {
  const {
    footer,
    isEditable,
    isSelected,
    objectLabels,
    presentationContentStyles,
    presetColors,
    setFieldValue,
    submitForm
  } = props
  const classes = useStyles({})
  const show = footer?.show ?? true
  const [isHovered, setIsHovered] = useState(false)

  // Toggled off in Settings → render nothing, in the editor as well as the customer view.
  if (!show) return null

  const block: PresentationBlock = {
    key: GLOBAL_FOOTER_BLOCK_KEY,
    type: '2-column',
    label: '',
    title: '',
    fields: {
      leftType: 'text',
      rightType: 'text',
      leftText: footer?.fields?.leftText ?? '',
      rightText: footer?.fields?.rightText ?? '',
      columnAlign: footer?.fields?.columnAlign ?? 'center'
    },
    background: footer?.background ?? {}
  }

  return (
    <div
      className={classnames(classes.root, {
        [classes.selected]: isSelected,
        [classes.hovered]: isEditable && isHovered && !isSelected
      })}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      data-testid={'global-footer'}
    >
      {isEditable && <div className={classes.pill}>Global footer — every page</div>}
      <TwoColumnBlock
        isEditable={isEditable && isSelected}
        isSettings={isEditable}
        objectLabels={objectLabels}
        presentationContentStyles={presentationContentStyles}
        presetColors={presetColors}
        block={block}
        blockPrefix={'advanced.footer'}
        setFieldValue={setFieldValue}
        submitForm={submitForm}
        WrapperComponent={FooterBlockWrapper}
      />
    </div>
  )
}

export default GlobalFooter
